import orderBy from 'lodash/orderBy'
import moment from 'moment'

import { update } from 'src/helpers'
import {
  DetailedOrderFragment,
  LineFragment,
  Maybe,
  SendDelayDelayType,
  SendDelayFragment,
  SendDelayTimeType,
  SendDelayType,
} from 'src/graphql'

export type PartialLine = Omit<
  LineFragment,
  'baseCost' | 'cost' | 'recipientCount'
> &
  Partial<Pick<LineFragment, 'baseCost' | 'cost' | 'recipientCount'>>

export const mapLines = <Line, Obj extends { lines: Line[] }>(
  obj: Obj,
  activeLine: Line,
  transform: (line: Line) => Line,
) => {
  const activeIndex = obj.lines.indexOf(activeLine)
  const newLines = update(obj.lines, activeIndex, transform)
  return {
    ...obj,
    lines: newLines,
  }
}

/*
  Sorts by sendDelay.type primarily,
  then sendDelay.delayType,
  sendDelay.delayNumber,
  sendDelay.specificDate,
  finally by card.id
*/
export const sortLines = <
  T extends {
    lines: { card?: Maybe<{ id: string }>; sendDelay: SendDelayFragment }[]
  }
>(
  obj: T,
): T => {
  const delayPriority = {
    IMM: 0,
    ANN: 1,
    BIR: 2,
    SPE: 3,
  }
  const intervalPriority = {
    DAY: 0,
    WEE: 1,
    MON: 2,
  }
  const sortedLines = orderBy(
    obj.lines,
    [
      e => {
        return delayPriority[e.sendDelay.type]
      },
      e => {
        return intervalPriority[e.sendDelay.delayType]
      },
      e => {
        return e.sendDelay.delayNumber
      },
      e => {
        return e.sendDelay.specificDate
      },
      e => {
        return e.card ? e.card.id : 0
      },
    ],
    ['asc', 'asc', 'asc', 'asc', 'asc'],
  )
  return { ...obj, lines: sortedLines }
}

export const findActiveIndex = (
  obj: { lines: Array<{ card: DetailedOrderFragment['lines'][0]['card'] }> },
  activeLine: PartialLine,
) => {
  return obj.lines.length >= 1
    ? obj.lines.reduce((activeIndex, line, index) => {
        const card = line.card
        const activeCard = activeLine.card
        return card?.id === activeCard?.id ||
          (card &&
            card.detailedSendableCard &&
            activeCard &&
            activeCard.detailedSendableCard &&
            card.detailedSendableCard.id === activeCard.detailedSendableCard.id)
          ? index
          : activeIndex
      }, 0)
    : -1
}

export const addLine = <Card>(card: Card) => {
  return {
    giftVariation: null,
    sendDelay: {
      delayNumber: 0,
      delayType: SendDelayDelayType.DAY,
      timeType: SendDelayTimeType.BEF,
      type: SendDelayType.IMM,
      specificDate: moment(new Date()).format('YYYY-MM-DD'),
    },
    card,
  }
}
